import { classifyRateLimited, pickWording, screenErrors } from '@/features/ga4-shared/errors'
import { ApiError } from '@/lib/api'

import { FIRST_PARTY_REFUSALS, FIRST_PARTY_WORDING } from '../traffic/errors'

/*
 * A failed read of the competition's page in words a person can act on (the routes'
 * errors, `competition_detail/README.md` *Errors*), on the app's shared screen-error
 * helper and the first-party read models' own refusals. An unknown failure is an
 * outage, never an empty tab, and the server's own text is never shown.
 */

/** The 404: no such competition in this tenant, or not on the website the filter bar names. */
export function isNotFound(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404
}

export const UNAVAILABLE = 'This could not be loaded just now. Try again in a moment.'

function detail(error: ApiError, key: string): string | null {
  const value = error.details[key]
  return typeof value === 'string' && value ? value : null
}

/** A 403 for a suspension (`details.reason`: `website_suspended`, `tenant_suspended`), and whose. */
function suspension(error: ApiError): 'website_suspended' | 'tenant_suspended' | null {
  const reason = detail(error, 'reason')
  if (reason === 'tenant_suspended') return 'tenant_suspended'
  return reason?.endsWith('_suspended') ? 'website_suspended' : null
}

/** The range refusal: the timewindow's `details.max_days`, or its "covers at most N days". */
function rangeTooLong(error: ApiError): boolean {
  return typeof error.details.max_days === 'number' || FIRST_PARTY_REFUSALS.rangeTooLong.test(error.message)
}

const screen = screenErrors<
  | 'forbidden'
  | 'website_suspended'
  | 'tenant_suspended'
  | 'range_too_long'
  | 'refused'
  | 'fx_unavailable'
  | 'rate_limited'
  | 'unavailable'
>({
  status: {
    403: (error) => suspension(error) ?? 'forbidden',
    // Any other 422 (a hand-edited id over 64 characters, an unknown parameter) is the link's, not the range's.
    422: (error) => (rangeTooLong(error) ? 'range_too_long' : 'refused'),
    // The README's 503 is a currency rate the server would not guess; any other falls to the outage.
    503: (error) => (FIRST_PARTY_REFUSALS.fxUnavailable.test(error.message) ? 'fx_unavailable' : null),
  },
  // Last, every other refusal: the outage's words, never the server's own text.
  rules: [classifyRateLimited, () => 'unavailable'],
  wording: {
    unavailable: UNAVAILABLE,
    forbidden: 'Your role cannot see analytics. Ask an Owner or Administrator for access.',
    website_suspended:
      'This competition’s website is suspended, so its figures cannot be shown. An Owner can contact TmT support.',
    tenant_suspended:
      'This organisation’s account is suspended, so its figures cannot be shown. An Owner can contact TmT support.',
    refused: 'This link could not be read. Check the address, or open the competition again from the competitions list.',
    ...FIRST_PARTY_WORDING,
    ...pickWording('rate_limited'),
  },
})

/**
 * The 422 that names the environment to add: the competition's own
 * (`details.competition_environment`), or the filter bar's website's
 * (`details.website_environment`).
 */
function environmentRefusal(error: unknown): string | null {
  if (!(error instanceof ApiError) || error.status !== 422) return null
  const competition = detail(error, 'competition_environment')
  if (competition) {
    return `This competition is on the ${competition} environment. Add ${competition} in the filter bar’s environments to see it.`
  }
  const website = detail(error, 'website_environment')
  if (website) {
    return `The website chosen in the filter bar is on the ${website} environment. Add ${website} in the filter bar’s environments, or choose another website.`
  }
  return null
}

export function competitionDetailErrorMessage(error: unknown): string {
  return environmentRefusal(error) ?? screen(error, null, UNAVAILABLE)
}
